// src/pages/admin/ContactMessages.jsx - Complete Enhanced Version
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FiMail,
  FiCheckCircle,
  FiCircle,
  FiArchive,
  FiTrash2,
  FiFlag,
  FiStar,
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiChevronLeft,
  FiChevronRight,
  FiMessageSquare,
  FiUser,
  FiCalendar,
  FiEye,
  FiTrendingUp,
  FiDownload,
  FiGrid,
  FiList,
  FiChevronDown,
  FiChevronUp,
  FiBarChart2,
  FiPieChart,
  FiCheck,
  FiFileText,
  FiTag,
  FiAtSign,
  FiLoader,
  FiX,
} from 'react-icons/fi';
import { LuFlagOff } from 'react-icons/lu';
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import contactService from '../../services/contactService';
import ContactMessageModal from '../../components/admin/ContactMessageModal';

// Custom Tooltip for Charts
const CustomTooltip = ({ active, payload, label, isDark }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="p-3 rounded-lg shadow-lg border"
        style={{
          backgroundColor: isDark ? '#1f2937' : '#ffffff',
          borderColor: isDark ? '#374151' : '#e5e7eb',
        }}
      >
        <p
          className="text-sm font-semibold"
          style={{ color: isDark ? '#f1f5f9' : '#1e293b' }}
        >
          {label}
        </p>
        <p
          className="text-xs mt-1"
          style={{ color: isDark ? '#94a3b8' : '#64748b' }}
        >
          Messages:{' '}
          <span className="font-bold" style={{ color: '#3b82f6' }}>
            {payload[0].value}
          </span>
        </p>
      </div>
    );
  }
  return null;
};

// Custom Label for Pie Chart
const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  index,
  name,
  value,
  isDark,
}) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill={isDark ? '#f1f5f9' : '#1e293b'}
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      className="text-xs font-medium"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

function ContactMessages() {
  const { actualTheme } = useTheme();
  const { currentUser } = useAuth();
  const isDark = actualTheme === 'dark';

  // ==================== STATE ====================
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    unread: 0,
    read: 0,
    archived: 0,
    spam: 0,
  });
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [timelineData, setTimelineData] = useState([]);
  const [timelineRange, setTimelineRange] = useState('30days');
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [dateRange, setDateRange] = useState('all');
  const [hasNotesFilter, setHasNotesFilter] = useState('all');
  const itemsPerPage = 20;

  // ==================== CHART COLORS ====================
  const chartColors = useMemo(
    () => ({
      primary: '#3b82f6',
      primaryLight: '#60a5fa',
      secondary: '#ec4899',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#06b6d4',
      purple: '#8b5cf6',
      text: isDark ? '#f1f5f9' : '#1e293b',
      textSecondary: isDark ? '#94a3b8' : '#64748b',
      grid: isDark ? '#334155' : '#e2e8f0',
      tooltipBg: isDark ? '#1e293b' : '#ffffff',
      tooltipBorder: isDark ? '#334155' : '#e2e8f0',
    }),
    [isDark],
  );

  const PIE_COLORS = [
    chartColors.warning,
    chartColors.success,
    chartColors.error,
    chartColors.primary,
  ];

  // ==================== LOAD DATA ====================
  const loadMessages = useCallback(async () => {
    try {
      setLoading(true);
      const result = await contactService.getMessages({}, null, 100);

      let messagesList = result.messages || [];

      // Apply status filter
      if (statusFilter !== 'all') {
        if (statusFilter === 'archived') {
          messagesList = messagesList.filter(m => m.isArchived === true);
        } else if (statusFilter === 'spam') {
          messagesList = messagesList.filter(m => m.isSpam === true);
        } else {
          messagesList = messagesList.filter(m => m.status === statusFilter);
        }
      }

      // Apply date range filter
      if (dateRange !== 'all') {
        const now = new Date();
        const cutoff = new Date();
        if (dateRange === 'today') cutoff.setHours(0, 0, 0, 0);
        else if (dateRange === 'week') cutoff.setDate(now.getDate() - 7);
        else if (dateRange === 'month') cutoff.setMonth(now.getMonth() - 1);
        else if (dateRange === 'quarter') cutoff.setMonth(now.getMonth() - 3);

        messagesList = messagesList.filter(m => {
          const date = m.createdAt?.toDate?.() || new Date(m.createdAt);
          return date >= cutoff;
        });
      }

      // Apply has notes filter
      if (hasNotesFilter !== 'all') {
        if (hasNotesFilter === 'has-notes') {
          messagesList = messagesList.filter(m => m.notes?.length > 0);
        } else if (hasNotesFilter === 'no-notes') {
          messagesList = messagesList.filter(
            m => !m.notes || m.notes.length === 0,
          );
        }
      }

      // Apply search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        messagesList = messagesList.filter(
          m =>
            m.name?.toLowerCase().includes(term) ||
            m.email?.toLowerCase().includes(term) ||
            m.subject?.toLowerCase().includes(term) ||
            m.message?.toLowerCase().includes(term) ||
            m.status?.toLowerCase().includes(term),
        );
      }

      // Apply sorting
      messagesList.sort((a, b) => {
        let aVal, bVal;
        switch (sortBy) {
          case 'date':
            aVal = a.createdAt?.toDate?.() || new Date(a.createdAt);
            bVal = b.createdAt?.toDate?.() || new Date(b.createdAt);
            break;
          case 'name':
            aVal = a.name || '';
            bVal = b.name || '';
            break;
          case 'status':
            aVal = a.status || '';
            bVal = b.status || '';
            break;
          case 'subject':
            aVal = a.subject || '';
            bVal = b.subject || '';
            break;
          default:
            aVal = a.createdAt?.toDate?.() || new Date(a.createdAt);
            bVal = b.createdAt?.toDate?.() || new Date(b.createdAt);
        }
        if (sortOrder === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });

      setMessages(messagesList);
      setHasMore(false);

      // Load statistics
      const statsData = await contactService.getStats();
      setStats(statsData);

      // Load timeline data
      let days = 30;
      if (timelineRange === '90days') days = 90;
      if (timelineRange === 'lifetime') days = 365 * 5;
      const timeline = await contactService.getTimelineData(days);
      setTimelineData(timeline);
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [
    statusFilter,
    dateRange,
    hasNotesFilter,
    searchTerm,
    sortBy,
    sortOrder,
    timelineRange,
  ]);

  useEffect(() => {
    loadMessages();
  }, [
    statusFilter,
    dateRange,
    hasNotesFilter,
    sortBy,
    sortOrder,
    timelineRange,
  ]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      loadMessages();
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadMessages();
  };

  // ==================== EXPORT ====================
  const handleExport = (format = 'csv') => {
    const data = messages.map(message => ({
      Name: message.name,
      Email: message.email,
      Subject: message.subject || 'No Subject',
      Status: message.isSpam
        ? 'Spam'
        : message.isArchived
          ? 'Archived'
          : message.status === 'unread'
            ? 'Unread'
            : 'Read',
      'Has Notes': message.notes?.length > 0 ? 'Yes' : 'No',
      'Notes Count': message.notes?.length || 0,
      'Has Response': message.responseMessage ? 'Yes' : 'No',
      'Email Response Sent': message.emailResponseSent ? 'Yes' : 'No',
      'Submitted At': message.createdAt?.toDate?.().toLocaleString() || '',
      Message: message.message,
    }));

    if (format === 'csv') {
      const headers = Object.keys(data[0] || {});
      const csv = [
        headers.join(','),
        ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(',')),
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contact-messages-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } else if (format === 'json') {
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contact-messages-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  // ==================== MESSAGE ACTIONS ====================
  const handleViewMessage = message => {
    setSelectedMessage(message);
    setIsModalOpen(true);

    if (message.status === 'unread' && !message.isSpam) {
      contactService.updateMessageStatus(message.id, 'read', currentUser?.uid);
      setMessages(prev =>
        prev.map(m => (m.id === message.id ? { ...m, status: 'read' } : m)),
      );
    }
  };

  const handleStatusUpdate = async (id, status, responseMessage = null) => {
    await contactService.updateMessageStatus(
      id,
      status,
      currentUser?.uid,
      responseMessage,
    );
    setMessages(prev => prev.map(m => (m.id === id ? { ...m, status } : m)));
    const statsData = await contactService.getStats();
    setStats(statsData);
    if (selectedMessage?.id === id) {
      setSelectedMessage(prev => ({ ...prev, status }));
    }
  };

  const handleArchive = async (id, isArchived) => {
    await contactService.toggleArchive(id, isArchived);
    setMessages(prev =>
      prev.map(m => (m.id === id ? { ...m, isArchived } : m)),
    );
    const statsData = await contactService.getStats();
    setStats(statsData);
    if (selectedMessage?.id === id) {
      setSelectedMessage(prev => ({ ...prev, isArchived }));
    }
  };

  const handleMarkSpam = async (id, isSpam) => {
    await contactService.markAsSpam(id, isSpam);
    setMessages(prev =>
      prev.map(m =>
        m.id === id ? { ...m, isSpam, status: isSpam ? 'spam' : 'unread' } : m,
      ),
    );
    const statsData = await contactService.getStats();
    setStats(statsData);
    if (selectedMessage?.id === id) {
      setSelectedMessage(prev => ({
        ...prev,
        isSpam,
        status: isSpam ? 'spam' : 'unread',
      }));
    }
  };

  const handleDelete = async id => {
    const message = messages.find(m => m.id === id);
    const notesCount = message?.notes?.length || 0;
    const hasResponse = message?.responseMessage ? 'Yes' : 'No';

    const confirmMessage = `Are you sure you want to delete this message?

This will permanently remove:
• The message itself
• ${notesCount} note(s)
• Response: ${hasResponse}

This action cannot be undone!`;

    if (!window.confirm(confirmMessage)) return;

    try {
      await contactService.deleteMessage(id);
      setMessages(prev => prev.filter(m => m.id !== id));
      const statsData = await contactService.getStats();
      setStats(statsData);
      if (selectedMessage?.id === id) {
        setIsModalOpen(false);
        setSelectedMessage(null);
      }
      setSelectedIds(prev => prev.filter(sid => sid !== id));
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Failed to delete message. Please try again.');
    }
  };

  // ==================== BULK SELECTION ====================
  const toggleSelect = id => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id],
    );
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
    } else {
      setSelectedIds(messages.map(m => m.id));
    }
    setSelectAll(!selectAll);
  };

  const clearSelection = () => {
    setSelectedIds([]);
    setSelectAll(false);
  };

  // ==================== BULK DELETE ====================
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;

    let totalNotes = 0;
    let totalWithResponse = 0;

    selectedIds.forEach(id => {
      const message = messages.find(m => m.id === id);
      if (message) {
        totalNotes += message.notes?.length || 0;
        if (message.responseMessage) totalWithResponse++;
      }
    });

    const confirmMessage = `Are you sure you want to delete ${selectedIds.length} selected message(s)?

This will permanently remove:
• ${selectedIds.length} message(s)
• ${totalNotes} note(s)
• ${totalWithResponse} response(s)
• All associated notifications

This action cannot be undone!`;

    if (!window.confirm(confirmMessage)) return;

    try {
      let deletedCount = 0;
      let failedCount = 0;
      let totalNotificationsDeleted = 0;

      for (const id of selectedIds) {
        try {
          // Use cascadeDeleteMessage which deletes everything
          const result = await contactService.cascadeDeleteMessage(id);
          deletedCount++;
          totalNotificationsDeleted += result.notifications || 0;
        } catch (err) {
          failedCount++;
          console.error(`Failed to delete message ${id}:`, err);
        }
      }

      alert(
        `✅ ${deletedCount} message(s) deleted successfully!\n` +
          `🗑️ ${totalNotificationsDeleted} notification(s) removed.` +
          (failedCount > 0 ? `\n⚠️ ${failedCount} failed to delete.` : ''),
      );
      clearSelection();
      loadMessages();
    } catch (error) {
      console.error('Error in bulk delete:', error);
      alert('Failed to delete selected messages');
    }
  };

  const handleBulkAction = async (action, value = null) => {
    if (selectedIds.length === 0) return;

    const requiresConfirmation = action === 'delete';
    if (
      requiresConfirmation &&
      !window.confirm(
        `Delete ${selectedIds.length} message(s)? This action cannot be undone.`,
      )
    )
      return;

    try {
      if (action === 'delete') {
        // Use cascade delete for each item
        let deletedCount = 0;
        let failedCount = 0;
        let totalNotifications = 0;

        for (const id of selectedIds) {
          try {
            const result = await contactService.cascadeDeleteMessage(id);
            deletedCount++;
            totalNotifications += result.notifications || 0;
          } catch (err) {
            failedCount++;
            console.error(`Failed to delete message ${id}:`, err);
          }
        }

        setMessages(prev => prev.filter(m => !selectedIds.includes(m.id)));
        clearSelection();
        alert(
          `✅ ${deletedCount} message(s) deleted!\n` +
            `🗑️ ${totalNotifications} notification(s) removed.` +
            (failedCount > 0 ? `\n⚠️ ${failedCount} failed.` : ''),
        );
      } else {
        // For non-delete actions, use the original bulkAction
        await contactService.bulkAction(selectedIds, action, value);

        if (action === 'mark-read') {
          setMessages(prev =>
            prev.map(m =>
              selectedIds.includes(m.id) ? { ...m, status: 'read' } : m,
            ),
          );
        } else if (action === 'mark-unread') {
          setMessages(prev =>
            prev.map(m =>
              selectedIds.includes(m.id) ? { ...m, status: 'unread' } : m,
            ),
          );
        } else if (action === 'archive') {
          setMessages(prev =>
            prev.map(m =>
              selectedIds.includes(m.id) ? { ...m, isArchived: true } : m,
            ),
          );
        } else if (action === 'unarchive') {
          setMessages(prev =>
            prev.map(m =>
              selectedIds.includes(m.id) ? { ...m, isArchived: false } : m,
            ),
          );
        } else if (action === 'spam') {
          setMessages(prev =>
            prev.map(m =>
              selectedIds.includes(m.id)
                ? { ...m, isSpam: true, status: 'spam' }
                : m,
            ),
          );
        } else if (action === 'not-spam') {
          setMessages(prev =>
            prev.map(m =>
              selectedIds.includes(m.id)
                ? { ...m, isSpam: false, status: 'unread' }
                : m,
            ),
          );
        }
        clearSelection();
      }

      const statsData = await contactService.getStats();
      setStats(statsData);
      console.log(
        `✅ Bulk action "${action}" completed on ${selectedIds.length} items`,
      );
    } catch (error) {
      console.error('Bulk action error:', error);
      alert(`Failed to ${action} selected items. Please try again.`);
    }
  };

  // ==================== UTILITY FUNCTIONS ====================
  const getStatusBadge = message => {
    if (message.isSpam) {
      return {
        label: 'Spam',
        color: '#ef4444',
        bgOpacity: '15%',
        icon: FiFlag,
      };
    }
    if (message.isArchived) {
      return {
        label: 'Archived',
        color: '#6b7280',
        bgOpacity: '15%',
        icon: FiArchive,
      };
    }
    if (message.status === 'unread') {
      return {
        label: 'Unread',
        color: '#3b82f6',
        bgOpacity: '15%',
        icon: FiCircle,
      };
    }
    if (message.status === 'responded') {
      return {
        label: 'Responded',
        color: '#8b5cf6',
        bgOpacity: '15%',
        icon: FiMessageSquare,
      };
    }
    return {
      label: 'Read',
      color: '#10b981',
      bgOpacity: '15%',
      icon: FiCheckCircle,
    };
  };

  const filters = [
    { id: 'all', label: 'All', count: stats.total, icon: FiMail },
    { id: 'unread', label: 'Unread', count: stats.unread, icon: FiCircle },
    { id: 'read', label: 'Read', count: stats.read, icon: FiCheckCircle },
    {
      id: 'archived',
      label: 'Archived',
      count: stats.archived,
      icon: FiArchive,
    },
    { id: 'spam', label: 'Spam', count: stats.spam, icon: FiFlag },
  ];

  const formatDate = timestamp => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString();
    } catch {
      return 'Invalid Date';
    }
  };

  // ==================== CHART DATA ====================
  const chartData = useMemo(() => {
    return timelineData.map(item => ({
      date: item.date,
      count: item.count,
      name: new Date(item.date).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      }),
    }));
  }, [timelineData]);

  const statusChartData = useMemo(() => {
    return [
      { name: 'Unread', value: stats.unread, color: chartColors.primary },
      { name: 'Read', value: stats.read, color: chartColors.success },
      { name: 'Archived', value: stats.archived, color: chartColors.warning },
      { name: 'Spam', value: stats.spam, color: chartColors.error },
    ].filter(item => item.value > 0);
  }, [stats, chartColors]);

  // ==================== MESSAGE CARD ====================
  const MessageCard = ({ message }) => {
    const statusBadge = getStatusBadge(message);
    const StatusIcon = statusBadge.icon;
    const isSelected = selectedIds.includes(message.id);

    return (
      <div
        className={`group rounded-xl border transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${
          isSelected ? 'ring-2 ring-blue-500' : ''
        } ${message.status === 'unread' && !message.isArchived && !message.isSpam ? 'border-l-4' : ''}`}
        style={{
          backgroundColor: 'var(--bg)',
          borderColor: 'var(--border)',
          borderLeftColor:
            message.status === 'unread' &&
            !message.isArchived &&
            !message.isSpam
              ? '#3b82f6'
              : 'var(--border)',
        }}
      >
        <div className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggleSelect(message.id)}
                className="w-4 h-4 rounded"
                style={{ accentColor: 'var(--text)' }}
              />
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'var(--border)' }}
              >
                <FiUser className="w-5 h-5" style={{ color: 'var(--text)' }} />
              </div>
            </div>
            {message.notes?.length > 0 && (
              <span
                className="px-2 py-0.5 rounded-full text-xs flex items-center gap-1"
                style={{
                  backgroundColor: 'var(--border)',
                  color: 'var(--muted)',
                }}
              >
                <FiFileText className="w-3 h-3" />
                {message.notes.length}
              </span>
            )}
          </div>

          <div className="mb-3">
            <h3
              className={`${message.status === 'unread' && !message.isArchived && !message.isSpam ? 'font-bold' : 'font-semibold'} text-base`}
              style={{ color: 'var(--text)' }}
            >
              {message.name}
            </h3>
            <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>
              {message.email}
            </p>
          </div>

          {message.subject && (
            <div className="mb-2">
              <p
                className="text-xs font-medium"
                style={{ color: 'var(--text)' }}
              >
                Subject:{' '}
                <span className="font-normal" style={{ color: 'var(--muted)' }}>
                  {message.subject}
                </span>
              </p>
            </div>
          )}

          <p
            className="text-sm line-clamp-2 mb-3"
            style={{ color: 'var(--muted)' }}
          >
            {message.message}
          </p>

          <div
            className="flex items-center justify-between pt-3 border-t"
            style={{ borderColor: 'var(--border)' }}
          >
            <div className="flex items-center gap-1">
              <span
                className="px-2 py-0.5 rounded-full text-xs flex items-center gap-1"
                style={{
                  backgroundColor: `${statusBadge.color}${statusBadge.bgOpacity}`,
                  color: statusBadge.color,
                }}
              >
                <StatusIcon className="w-2 h-2" />
                {statusBadge.label}
              </span>
              <span
                className="text-xs flex items-center gap-1"
                style={{ color: 'var(--muted)' }}
              >
                <FiCalendar className="w-3 h-3" />
                {formatDate(message.createdAt)}
              </span>
            </div>
            <button
              onClick={() => handleViewMessage(message)}
              className="p-1.5 rounded-lg transition-colors hover:bg-black/5 dark:hover:bg-white/5"
            >
              <FiEye className="w-4 h-4" style={{ color: 'var(--muted)' }} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ==================== MESSAGE LIST ITEM ====================
  const MessageListItem = ({ message }) => {
    const statusBadge = getStatusBadge(message);
    const StatusIcon = statusBadge.icon;
    const isSelected = selectedIds.includes(message.id);

    return (
      <div
        className={`p-4 rounded-xl border transition-all duration-200 hover:shadow-md ${
          isSelected ? 'ring-2 ring-blue-500' : ''
        } ${message.status === 'unread' && !message.isArchived && !message.isSpam ? 'border-l-4' : ''}`}
        style={{
          backgroundColor: 'var(--bg)',
          borderColor: 'var(--border)',
          borderLeftColor:
            message.status === 'unread' &&
            !message.isArchived &&
            !message.isSpam
              ? '#3b82f6'
              : 'var(--border)',
        }}
      >
        <div className="flex items-start gap-4">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => toggleSelect(message.id)}
              className="mt-1 w-4 h-4 rounded"
              style={{ accentColor: 'var(--text)' }}
            />
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <FiUser className="w-5 h-5" style={{ color: 'var(--text)' }} />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3
                  className={`${message.status === 'unread' && !message.isArchived && !message.isSpam ? 'font-bold' : 'font-semibold'}`}
                  style={{ color: 'var(--text)' }}
                >
                  {message.name}
                </h3>
                <span style={{ color: 'var(--muted)' }}>•</span>
                <a
                  href={`mailto:${message.email}`}
                  className="text-sm hover:underline"
                  style={{ color: 'var(--muted)' }}
                >
                  {message.email}
                </a>
                {message.notes?.length > 0 && (
                  <span
                    className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--muted)',
                    }}
                  >
                    <FiFileText className="w-3 h-3" />
                    {message.notes.length}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span
                  className="px-2 py-0.5 rounded-full text-xs flex items-center gap-1"
                  style={{
                    backgroundColor: `${statusBadge.color}${statusBadge.bgOpacity}`,
                    color: statusBadge.color,
                  }}
                >
                  <StatusIcon className="w-2 h-2" />
                  {statusBadge.label}
                </span>
                <span
                  className="text-xs flex items-center gap-1"
                  style={{ color: 'var(--muted)' }}
                >
                  <FiCalendar className="w-3 h-3" />
                  {formatDate(message.createdAt)}
                </span>
              </div>
            </div>

            {message.subject && (
              <p
                className="text-sm font-medium mb-1"
                style={{ color: 'var(--text)' }}
              >
                Subject:{' '}
                <span className="font-normal" style={{ color: 'var(--muted)' }}>
                  {message.subject}
                </span>
              </p>
            )}

            <p
              className="text-sm line-clamp-2 mb-3"
              style={{ color: 'var(--muted)' }}
            >
              {message.message}
            </p>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleViewMessage(message)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs transition-all hover:opacity-80"
                style={{
                  backgroundColor: 'var(--border)',
                  color: 'var(--text)',
                }}
              >
                <FiEye className="w-3 h-3" />
                View Details
              </button>
              {message.status !== 'read' &&
                message.status !== 'responded' &&
                !message.isArchived &&
                !message.isSpam && (
                  <button
                    onClick={() => handleStatusUpdate(message.id, 'read')}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs transition-all hover:opacity-80"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                  >
                    <FiCheckCircle className="w-3 h-3" />
                    Mark Read
                  </button>
                )}
              {!message.isArchived && !message.isSpam && (
                <button
                  onClick={() => handleStatusUpdate(message.id, 'responded')}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs transition-all hover:opacity-80"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  <FiMessageSquare className="w-3 h-3" />
                  Mark Responded
                </button>
              )}
              {!message.isArchived && !message.isSpam ? (
                <button
                  onClick={() => handleArchive(message.id, true)}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs transition-all hover:opacity-80"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  <FiArchive className="w-3 h-3" />
                  Archive
                </button>
              ) : message.isArchived && !message.isSpam ? (
                <button
                  onClick={() => handleArchive(message.id, false)}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs transition-all hover:opacity-80"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  <FiArchive className="w-3 h-3" />
                  Unarchive
                </button>
              ) : null}
              {!message.isSpam ? (
                <button
                  onClick={() => handleMarkSpam(message.id, true)}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs transition-all hover:opacity-80"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  <FiFlag className="w-3 h-3" />
                  Mark Spam
                </button>
              ) : (
                <button
                  onClick={() => handleMarkSpam(message.id, false)}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs transition-all hover:opacity-80"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  <LuFlagOff className="w-3 h-3" />
                  Not Spam
                </button>
              )}
              <button
                onClick={() => handleDelete(message.id)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs transition-all hover:opacity-80"
                style={{ backgroundColor: '#ef4444', color: 'white' }}
              >
                <FiTrash2 className="w-3 h-3" />
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ==================== RENDER ====================
  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>
            Contact Messages
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>
            Manage and respond to messages from your website visitors
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {selectedIds.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80 bg-red-500 text-white"
            >
              <FiTrash2 className="w-4 h-4" />
              <span className="text-sm">
                Delete Selected ({selectedIds.length})
              </span>
            </button>
          )}
          <button
            onClick={() => setShowAnalytics(!showAnalytics)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80 ${showAnalytics ? 'bg-black/10 dark:bg-white/10' : ''}`}
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            <FiTrendingUp className="w-4 h-4" />
            <span className="text-sm">Analytics</span>
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80 ${showFilters ? 'bg-black/10 dark:bg-white/10' : ''}`}
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            <FiFilter className="w-4 h-4" />
            <span className="text-sm">Filters</span>
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80"
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            <FiRefreshCw
              className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`}
            />
            <span className="text-sm">Refresh</span>
          </button>
        </div>
      </div>

      {/* Analytics Dashboard */}
      {showAnalytics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-in">
          {/* Status Distribution - Pie Chart */}
          <div
            className="p-5 rounded-2xl border"
            style={{
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3
                className="font-semibold flex items-center gap-2"
                style={{ color: chartColors.text }}
              >
                <FiPieChart
                  className="w-4 h-4"
                  style={{ color: chartColors.primary }}
                />
                Message Status Distribution
              </h3>
              <span
                className="text-xs"
                style={{ color: chartColors.textSecondary }}
              >
                Total: {stats.total} messages
              </span>
            </div>
            {statusChartData.length > 0 ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={props =>
                        renderCustomizedLabel({ ...props, isDark })
                      }
                    >
                      {statusChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: chartColors.tooltipBg,
                        borderColor: chartColors.tooltipBorder,
                        borderRadius: '8px',
                      }}
                      formatter={(value, name, props) => {
                        const total = statusChartData.reduce(
                          (sum, item) => sum + item.value,
                          0,
                        );
                        const percentage = ((value / total) * 100).toFixed(1);
                        return [`${value} messages (${percentage}%)`, name];
                      }}
                      labelStyle={{ color: chartColors.text }}
                    />
                    <Legend
                      formatter={(value, entry, index) => (
                        <span
                          style={{
                            color: chartColors.textSecondary,
                            fontSize: '11px',
                          }}
                        >
                          {value}
                        </span>
                      )}
                      wrapperStyle={{ fontSize: '11px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-80 flex items-center justify-center">
                <p
                  className="text-sm"
                  style={{ color: chartColors.textSecondary }}
                >
                  No status data available
                </p>
              </div>
            )}
            <div
              className="mt-3 pt-2 border-t flex justify-between text-xs"
              style={{
                borderColor: chartColors.grid,
                color: chartColors.textSecondary,
              }}
            >
              <span>
                Response Rate:{' '}
                <strong
                  className="font-bold"
                  style={{ color: chartColors.success }}
                >
                  {stats.total > 0
                    ? ((stats.read / stats.total) * 100).toFixed(1)
                    : 0}
                  %
                </strong>
              </span>
              <span>
                Unread:{' '}
                <strong
                  className="font-bold"
                  style={{ color: chartColors.warning }}
                >
                  {stats.unread}
                </strong>
              </span>
            </div>
          </div>

          {/* Timeline Chart - Area Chart */}
          <div
            className="p-5 rounded-2xl border"
            style={{
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3
                className="font-semibold flex items-center gap-2"
                style={{ color: chartColors.text }}
              >
                <FiCalendar
                  className="w-4 h-4"
                  style={{ color: chartColors.info }}
                />
                Message Timeline
              </h3>
              <div className="flex gap-1">
                {['30days', '90days', 'lifetime'].map(range => (
                  <button
                    key={range}
                    onClick={() => setTimelineRange(range)}
                    className={`px-3 py-1 rounded-lg text-xs transition-all ${
                      timelineRange === range
                        ? 'bg-black/20 dark:bg-white/20 font-medium'
                        : 'hover:bg-black/5 dark:hover:bg-white/5'
                    }`}
                    style={{ color: chartColors.text }}
                  >
                    {range === '30days'
                      ? 'Last 30 Days'
                      : range === '90days'
                        ? 'Last 90 Days'
                        : 'Lifetime'}
                  </button>
                ))}
              </div>
            </div>
            {chartData.length > 0 ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={chartData}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="colorCount"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor={chartColors.primary}
                          stopOpacity={0.8}
                        />
                        <stop
                          offset="95%"
                          stopColor={chartColors.primary}
                          stopOpacity={0.1}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={chartColors.grid}
                    />
                    <XAxis
                      dataKey="name"
                      tick={{ fill: chartColors.textSecondary, fontSize: 11 }}
                      interval={
                        timelineRange === '30days'
                          ? 5
                          : timelineRange === '90days'
                            ? 14
                            : 30
                      }
                    />
                    <YAxis
                      tick={{ fill: chartColors.textSecondary, fontSize: 11 }}
                      allowDecimals={false}
                    />
                    <Tooltip content={<CustomTooltip isDark={isDark} />} />
                    <Area
                      type="monotone"
                      dataKey="count"
                      stroke={chartColors.primary}
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorCount)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-80 flex items-center justify-center">
                <p
                  className="text-sm"
                  style={{ color: chartColors.textSecondary }}
                >
                  No timeline data available
                </p>
              </div>
            )}
            <div
              className="mt-3 pt-2 border-t flex justify-between text-xs"
              style={{ borderColor: chartColors.grid }}
            >
              <span style={{ color: chartColors.textSecondary }}>
                Daily message volume
              </span>
              <span style={{ color: chartColors.textSecondary }}>
                Peak:{' '}
                <strong style={{ color: chartColors.primary }}>
                  {Math.max(...chartData.map(d => d.count), 0)}
                </strong>{' '}
                messages
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {filters.map(f => (
          <button
            key={f.id}
            onClick={() => setStatusFilter(f.id)}
            className={`p-3 rounded-xl text-center transition-all duration-200 hover:scale-105 ${
              statusFilter === f.id
                ? 'bg-black/10 dark:bg-white/10'
                : 'border hover:bg-black/5 dark:hover:bg-white/5'
            }`}
            style={{ borderColor: 'var(--border)' }}
          >
            <div className="flex items-center justify-center gap-1 mb-1">
              <f.icon className="w-3 h-3" style={{ color: 'var(--muted)' }} />
              <p className="text-xl font-bold" style={{ color: 'var(--text)' }}>
                {f.count}
              </p>
            </div>
            <p className="text-xs" style={{ color: 'var(--muted)' }}>
              {f.label}
            </p>
          </button>
        ))}
      </div>

      {/* Advanced Filters Panel */}
      {showFilters && (
        <div
          className="p-4 rounded-xl border animate-fade-in"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Date Range Filter */}
            <div>
              <label
                className="text-xs font-medium block mb-1"
                style={{ color: 'var(--muted)' }}
              >
                Date Range
              </label>
              <select
                value={dateRange}
                onChange={e => setDateRange(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm outline-none transition-all"
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
                <option value="quarter">Last 90 Days</option>
              </select>
            </div>

            {/* Has Notes Filter */}
            <div>
              <label
                className="text-xs font-medium block mb-1"
                style={{ color: 'var(--muted)' }}
              >
                Notes
              </label>
              <select
                value={hasNotesFilter}
                onChange={e => setHasNotesFilter(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm outline-none transition-all"
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              >
                <option value="all">All Messages</option>
                <option value="has-notes">Has Notes</option>
                <option value="no-notes">No Notes</option>
              </select>
            </div>

            {/* Sort Options */}
            <div>
              <label
                className="text-xs font-medium block mb-1"
                style={{ color: 'var(--muted)' }}
              >
                Sort By
              </label>
              <div className="flex gap-2">
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg text-sm outline-none transition-all"
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  <option value="date">Date</option>
                  <option value="name">Name</option>
                  <option value="status">Status</option>
                  <option value="subject">Subject</option>
                </select>
                <button
                  onClick={() =>
                    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
                  }
                  className="px-3 py-2 rounded-lg transition-all hover:bg-black/5 dark:hover:bg-white/5"
                  style={{
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  {sortOrder === 'asc' ? (
                    <FiChevronUp className="w-4 h-4" />
                  ) : (
                    <FiChevronDown className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div
            className="mt-4 pt-3 border-t flex flex-wrap gap-2"
            style={{ borderColor: 'var(--border)' }}
          >
            <button
              onClick={() => {
                setDateRange('all');
                setHasNotesFilter('all');
                setStatusFilter('all');
                setSearchTerm('');
              }}
              className="px-3 py-1.5 rounded-lg text-xs transition-all hover:opacity-80"
              style={{
                border: '1px solid var(--border)',
                color: 'var(--text)',
              }}
            >
              Clear All Filters
            </button>
            <button
              onClick={() => handleExport('csv')}
              className="px-3 py-1.5 rounded-lg text-xs transition-all hover:opacity-80"
              style={{
                border: '1px solid var(--border)',
                color: 'var(--text)',
              }}
            >
              <FiDownload className="inline w-3 h-3 mr-1" />
              Export CSV
            </button>
            <button
              onClick={() => handleExport('json')}
              className="px-3 py-1.5 rounded-lg text-xs transition-all hover:opacity-80"
              style={{
                border: '1px solid var(--border)',
                color: 'var(--text)',
              }}
            >
              <FiFileText className="inline w-3 h-3 mr-1" />
              Export JSON
            </button>
          </div>
        </div>
      )}

      {/* Filters Bar */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-1.5 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-black/10 dark:bg-white/10' : ''}`}
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            <FiGrid className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-1.5 rounded-lg transition-all ${viewMode === 'list' ? 'bg-black/10 dark:bg-white/10' : ''}`}
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            <FiList className="w-4 h-4" />
          </button>
        </div>
        {messages.length > 0 && (
          <span className="text-xs" style={{ color: 'var(--muted)' }}>
            {messages.length} message{messages.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Search Bar */}
      <div className="relative">
        <FiSearch
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
          style={{ color: 'var(--muted)' }}
        />
        <input
          type="text"
          placeholder="Search by name, email, subject, message, or status..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="w-full pl-9 pr-4 py-2 rounded-xl outline-none transition-all"
          style={{
            backgroundColor: 'transparent',
            border: '1px solid var(--border)',
            color: 'var(--text)',
          }}
        />
      </div>

      {/* Select All */}
      {messages.length > 0 && (
        <div className="flex items-center gap-3 px-1">
          <button
            onClick={toggleSelectAll}
            className="flex items-center gap-2 text-sm transition-all hover:opacity-80"
            style={{ color: 'var(--muted)' }}
          >
            <span
              className="w-4 h-4 rounded border-2 flex items-center justify-center"
              style={{ borderColor: 'var(--border)' }}
            >
              {selectAll && (
                <FiCheck className="w-3 h-3" style={{ color: 'var(--text)' }} />
              )}
            </span>
            {selectAll ? 'Deselect All' : 'Select All'}
            <span className="text-xs" style={{ color: 'var(--muted)' }}>
              ({messages.length} items)
            </span>
          </button>
          {selectedIds.length > 0 && (
            <span className="text-xs" style={{ color: 'var(--muted)' }}>
              • {selectedIds.length} selected
            </span>
          )}
        </div>
      )}

      {/* Bulk Actions Bar */}
      {selectedIds.length > 0 && (
        <div
          className="flex items-center justify-between p-3 rounded-xl animate-fade-in flex-wrap gap-2"
          style={{ backgroundColor: 'var(--border)' }}
        >
          <span className="text-sm" style={{ color: 'var(--text)' }}>
            {selectedIds.length} message(s) selected
          </span>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => handleBulkAction('mark-read')}
              className="px-3 py-1.5 rounded-lg text-sm transition-all hover:opacity-80"
              style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}
            >
              Mark Read
            </button>
            <button
              onClick={() => handleBulkAction('mark-unread')}
              className="px-3 py-1.5 rounded-lg text-sm transition-all hover:opacity-80"
              style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}
            >
              Mark Unread
            </button>
            <button
              onClick={() => handleBulkAction('archive')}
              className="px-3 py-1.5 rounded-lg text-sm transition-all hover:opacity-80"
              style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}
            >
              Archive
            </button>
            <button
              onClick={() => handleBulkAction('unarchive')}
              className="px-3 py-1.5 rounded-lg text-sm transition-all hover:opacity-80"
              style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}
            >
              Unarchive
            </button>
            <button
              onClick={() => handleBulkAction('spam')}
              className="px-3 py-1.5 rounded-lg text-sm transition-all hover:opacity-80"
              style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}
            >
              Mark Spam
            </button>
            <button
              onClick={() => handleBulkAction('not-spam')}
              className="px-3 py-1.5 rounded-lg text-sm transition-all hover:opacity-80"
              style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}
            >
              Not Spam
            </button>
            <button
              onClick={handleBulkDelete}
              className="px-3 py-1.5 rounded-lg text-sm transition-all hover:opacity-80"
              style={{ backgroundColor: '#ef4444', color: 'white' }}
            >
              Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Messages List/Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div
            className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: 'var(--text)' }}
          />
        </div>
      ) : messages.length === 0 ? (
        <div
          className="text-center py-12 rounded-2xl border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <FiMail
            className="w-12 h-12 mx-auto mb-3 opacity-50"
            style={{ color: 'var(--muted)' }}
          />
          <p style={{ color: 'var(--muted)' }}>No messages found</p>
          <button
            onClick={() => {
              setStatusFilter('all');
              setDateRange('all');
              setHasNotesFilter('all');
              setSearchTerm('');
            }}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm"
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            Clear All Filters
          </button>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {messages.map(message => (
            <MessageCard key={message.id} message={message} />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {messages.map(message => (
            <MessageListItem key={message.id} message={message} />
          ))}
        </div>
      )}

      {/* Load More */}
      {hasMore && !loading && (
        <div className="flex justify-center pt-4">
          <button
            onClick={loadMessages}
            className="px-6 py-2 rounded-xl text-sm transition-all hover:opacity-80"
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            Load More
          </button>
        </div>
      )}

      {/* Message Detail Modal */}
      {selectedMessage && (
        <ContactMessageModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedMessage(null);
          }}
          message={selectedMessage}
          onStatusUpdate={handleStatusUpdate}
          onDelete={handleDelete}
          onArchive={handleArchive}
          onMarkSpam={handleMarkSpam}
        />
      )}
    </div>
  );
}

export default ContactMessages;
